/**
 * EntityField - Generic field cell for entity change cards.
 *
 * Renders compact inline for unchanged fields, card-style with amber background for changed.
 */

import { formatEnumValue, formatFieldValue } from '../utils/formatting';
import type { EntityChanges } from '../utils/useEntityChanges';
import { ChangedFieldIndicator } from './ChangeIndicators';

export const EntityField: React.FC<{
  label: string;
  value: unknown;
  fieldKey: string;
  changes: EntityChanges;
  isDate?: boolean;
  isEnum?: boolean;
}> = ({ label, value, fieldKey, changes, isDate, isEnum }) => {
  const isChanged = changes.isFieldChanged(fieldKey);
  const displayValue = isEnum
    ? (formatEnumValue(value as string) ?? '\u2014')
    : formatFieldValue(value, isDate);

  if (isChanged) {
    return (
      <div className="rounded-md px-3 py-2 bg-amber-50/60">
        <div className="flex items-center gap-1 mb-0.5">
          <span className="text-xs font-medium text-gray-500">{label}</span>
          <ChangedFieldIndicator
            oldValue={changes.getOldValue(fieldKey)}
            label={label}
          />
        </div>
        <div className="text-sm text-gray-900">{displayValue}</div>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-1.5 px-1 py-0.5">
      <span className="text-xs text-gray-400 shrink-0">{label}:</span>
      <span className="text-sm text-gray-700 truncate">{displayValue}</span>
    </div>
  );
};
